import React, { useState } from 'react';
import { Link2, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const Integrations = () => {
  const [integrations, setIntegrations] = useState([
    {
      id: 'tevalis_pos',
      name: 'Tevalis POS',
      status: 'pending',
      description: 'Real-time revenue tracking from bar, food, and events',
      details: 'Pending: API credentials needed to activate full integration.',
      configured: false,
    },
    {
      id: 'clock_pms',
      name: 'Clock PMS',
      status: 'planned',
      description: 'Room availability, guest info, and room revenue tracking',
      details: 'Planned for future version.',
      configured: false,
    },
  ]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      connected: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircle,
        label: 'Connected',
      },
      pending: {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        icon: AlertCircle,
        label: 'Setup Required',
      },
      planned: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: Clock,
        label: 'Planned',
      },
    };

    const config = statusConfig[status];
    const IconComponent = config.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${config.bg} ${config.text}`}>
        <IconComponent size={16} />
        <span className="text-sm font-medium">{config.label}</span>
      </div>
    );
  };

  const handleConfigure = (integrationId) => {
    setIntegrations((prev) =>
      prev.map((int) =>
        int.id === integrationId
          ? { ...int, status: 'connected', configured: true }
          : int
      )
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Link2 size={32} className="text-emerald-600" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Connected Systems</h1>
        </div>
        <p className="text-gray-600">
          Manage integrations with third-party platforms and services
        </p>
      </div>

      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="p-4 md:p-6">
              {/* Header with Name and Status */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4 mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {integration.name}
                </h2>
                {getStatusBadge(integration.status)}
              </div>

              {/* Description */}
              <p className="text-gray-700 mb-4">{integration.description}</p>

              {/* Details Box */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                <p className="text-sm text-gray-700">{integration.details}</p>
              </div>

              {/* Configuration Summary */}
              <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  {integration.configured
                    ? 'Status: Connected'
                    : integration.status === 'planned'
                    ? 'Status: Scheduled for future release'
                    : 'Status: Awaiting API setup'}
                </p>
              </div>

              {/* Configure Button */}
              <button
                onClick={() => handleConfigure(integration.id)}
                disabled={integration.status === 'planned'}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  integration.status === 'planned'
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : integration.status === 'connected'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {integration.status === 'planned'
                  ? 'Coming Soon'
                  : integration.status === 'connected'
                  ? 'Configured'
                  : 'Configure'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* CRM Integration Note */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 md:p-6">
        <p className="text-emerald-900 font-medium">
          <span className="block font-semibold mb-2">Built-in CRM Functionality</span>
          The Errigal Events Manager includes built-in CRM functionality — no external CRM integration
          required. Manage all client information, contact history, and event details directly within
          the platform.
        </p>
      </div>
    </div>
  );
};

export default Integrations;
